import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import type { MfaChallengeResponse } from "@telecurb/shared";
import type { LoginStartResult, MfaEnrollRequest, MfaEnrollResult } from "./client";
import { workosAuthClient } from "./workos-client";
import { ME_QUERY_KEY } from "./queries";
import { LAST_ACTIVITY_KEY } from "@/components/IdleTimeout";

interface SignInVars {
  email: string;
  password: string;
}

// Runs once a session has actually been sealed (after MFA verify, OR at login on
// the Phase 2 trusted-device shortcut). Completes the /me bootstrap (staleTime: 0
// forces a fresh fetch past the Infinity-staleTime cache) so it seeds the cache
// RequireAuth reads on the next mount AND lets a broken session reject the
// sign-in. A missing_actor (authenticated at WorkOS but no DB actor — e.g. a
// dropped/reseeded dev DB or a cleared account) throws here instead of silently
// bouncing back to /sign-in (TEL-143). Anchors the idle timer last, only on
// success, so a new session never inherits the previous session's timestamp.
async function establishSession(qc: QueryClient): Promise<void> {
  let me;
  try {
    me = await qc.fetchQuery({
      queryKey: ME_QUERY_KEY,
      queryFn: () => workosAuthClient.getCurrentUser(),
      staleTime: 0,
    });
  } catch (err) {
    // A session was sealed; tear it down so the user isn't left half-authenticated
    // with an unusable cookie, then surface the error.
    await workosAuthClient.logout().catch(() => undefined);
    // Pin /me to the signed-out (null) state rather than removing it. SignIn's
    // /me gate (TEL-161) shows a Loading/error screen whenever this query is
    // pending or errored; removing it would flash that screen and unmount the
    // challenge step mid-error, swallowing the contact-support message. Setting
    // null leaves a fresh signed-out result so the step machine keeps rendering.
    qc.setQueryData(ME_QUERY_KEY, null);
    throw err;
  }
  // A null here right after a seal means the session didn't take (a non-
  // missing_actor 401). Don't navigate into a signed-out shell.
  if (!me) {
    await workosAuthClient.logout().catch(() => undefined);
    throw new Error("session_not_established");
  }
  localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
}

// Step 1 (TEL-112): the password check. Usually no session is sealed yet — the
// status tells the caller whether to enroll or go to the SMS challenge. Phase 2:
// a trusted device returns `authenticated` and the session IS sealed at login, so
// we run the bootstrap here too before handing the status back.
export function useSignIn(): UseMutationResult<LoginStartResult, Error, SignInVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ email, password }: SignInVars) => {
      const result = await workosAuthClient.login(email, password);
      if (result.status === "authenticated") await establishSession(qc);
      return result;
    },
  });
}

export function useEnrollMfa(): UseMutationResult<MfaEnrollResult, Error, MfaEnrollRequest> {
  return useMutation({
    mutationFn: (req: MfaEnrollRequest) => workosAuthClient.enrollMfa(req),
  });
}

export function useResendMfaChallenge(): UseMutationResult<MfaChallengeResponse, Error, void> {
  return useMutation({
    mutationFn: () => workosAuthClient.resendMfaChallenge(),
  });
}

export function useRestartMfaEnrollment(): UseMutationResult<void, Error, void> {
  return useMutation({
    mutationFn: () => workosAuthClient.restartMfaEnrollment(),
  });
}

// Final step: verify the SMS code. This is where the session is sealed (for the
// normal SMS path), so the /me bootstrap runs here. `rememberDevice` is forwarded
// to mint a trusted-device token (Phase 2). Mirrors the accept-invite path.
export function useVerifyMfa(): UseMutationResult<
  void,
  Error,
  { challengeId: string; code: string; rememberDevice?: boolean }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      challengeId,
      code,
      rememberDevice,
    }: {
      challengeId: string;
      code: string;
      rememberDevice?: boolean;
    }) => {
      await workosAuthClient.verifyMfa(challengeId, code, rememberDevice);
      await establishSession(qc);
    },
  });
}

export function useSignOut(): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => workosAuthClient.logout(),
    // Drop every cached query so the next sign-in in the same tab never
    // briefly renders the previous user's data. Also clear the idle anchor so a
    // stale timestamp can't force-sign-out the next user the instant they mount.
    onSuccess: () => {
      qc.clear();
      localStorage.removeItem(LAST_ACTIVITY_KEY);
    },
  });
}
